import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useAddCityLocationMutation,
  useLazyGetPincodesQuery,
} from "slices/common/locationSlice";
import { useNavigate } from "react-router-dom";
import PincodeChecker from "./pincodeChecker";
import Swal from "sweetalert2";

// ✅ Helper: Convert [lng, lat] to {lat, lng} for Google Maps
const getLatLng = (coords) => {
  if (!coords || coords.length < 2) return null;
  return { lat: parseFloat(coords[1]), lng: parseFloat(coords[0]) };
};

function AddLocation() {
  const navigate = useNavigate();
  const [addCity] = useAddCityLocationMutation();
  const [getPincodes, { data: pincodeData }] = useLazyGetPincodesQuery();

  const cityInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const [mapCenter, setMapCenter] = useState({ lat: 30.7333, lng: 76.7794 });
  const [message, setMessage] = useState("");

  const formik = useFormik({
    initialValues: {
      cityName: "",
      full_name: "",
      coordinates: [],
      metaTitle: "",
      metaDescription: "",
      keywords: [],
      areas: [],
      keywordInput: "",
      mainArray: [],
      gst_number: "",
      // subscription_amount: "",
      // booking_limit: "",
      // top_up_period: "",
    },
    validationSchema: Yup.object({
      cityName: Yup.string().required("City name is required"),
      metaTitle: Yup.string().required("Meta title is required"),
      metaDescription: Yup.string().required("Meta description is required"),
      gst_number: Yup.string().required("GST number is required"),
      // .required("GST number  is required")
      // .min(1, "GST number  cannot be 0 or negative"),

      // subscription_amount: Yup.number()
      //   .typeError("Subscription amount must be a number")
      //   .required("Subscription amount is required")
      //   .min(1, "Subscription amount cannot be 0 or negative"),

      // booking_limit: Yup.number()
      //   .typeError("Booking limit must be a number")
      //   .required("Booking limit is required")
      //   .min(1, "Booking limit cannot be 0 or negative"),

      // top_up_period: Yup.number()
      //   .typeError("Top-up period must be a number")
      //   .required("Top-up period is required")
      //   .min(1, "Top-up period cannot be 0 or negative"),
      keywords: Yup.array()
        .of(Yup.string().required("Keyword cannot be empty"))
        .min(1, "At least one keyword is required")
        .required("Keywords are required"),
      areas: Yup.array()
        .of(
          Yup.object().shape({
            pincode: Yup.string().required("Pincode is required"),
          })
        )
        .min(1, "At least one area is required") // <-- require at least one area
        .required("Areas are required"),
    }),
    onSubmit: async (values) => {
      const sortedArry =
        values.areas?.map((area) => ({
          areaName: area.areaName,
          pincode: area.pincode,
          location: {
            type: "Point",
            coordinates: area.coordinates, // [lng, lat] for backend
          },
        })) || [];

      const form = {
        cityName: values.cityName,
        full_name: values.full_name,
        location: {
          type: "Point",
          coordinates: values.coordinates,
        },
        metaTitle: values.metaTitle,
        metaDescription: values.metaDescription,
        keywords: values.keywords,
        areas: sortedArry,
        mainArray: values.mainArray,
        gst_number: values.gst_number,
        // subscription_amount: values.subscription_amount,
        // booking_limit: values.booking_limit,
        // top_up_period: values.top_up_period,
      };

      // await addCity(form).unwrap();
      // navigate("/settings/locations", { state: { refresh: true } });

      try {
        await addCity(form).unwrap();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Location added successfully",
        });
        setTimeout(() => {
          navigate("/settings/locations", { state: { refresh: true } });
        }, 1000);
      } catch (error) {
        // RTK Query errors can have .data.message or .error
        const message =
          error?.data?.message || error?.message || "Something went wrong";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: message,
        });
      }
    },
  });
  // City autocomplete
  useEffect(() => {
    if (window.google && cityInputRef.current) {
      const cityAutocomplete = new window.google.maps.places.Autocomplete(
        cityInputRef.current,
        {
          types: ["(cities)"],
          componentRestrictions: { country: "in" },
        }
      );

      cityAutocomplete.addListener("place_changed", () => {
        const place = cityAutocomplete.getPlace();
        if (place.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          markersRef.current.forEach((m) => m.setMap(null));
          markersRef.current = [];

          formik.setValues({
            ...formik.values, // ✅ preserve existing values
            cityName: place.name || place.formatted_address, // ✅ SET CITY NAME
            full_name: place.formatted_address,
            coordinates: [lng, lat],
            metaTitle: "",
            metaDescription: "",
            keywords: [],
            areas: [],
            keywordInput: "",
            gst_number: "",
          });

          // setMapCenter({ lat, lng });
          setMapCenter(getLatLng([lat, lng]));
          fetchPincodes(place.name);
        }
      });
    }
  }, []);

  // Render markers on map
  useEffect(() => {
    if (window.google && formik.values.areas.length > 0 && mapRef.current) {
      const map = new window.google.maps.Map(mapRef.current, {
        center: mapCenter,
        zoom: 12,
      });

      mapInstance.current = map;

      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];

      const infoWindow = new window.google.maps.InfoWindow();
      const bounds = new window.google.maps.LatLngBounds();

      formik.values.areas.forEach((area) => {
        const position = getLatLng(area.coordinates);
        if (!position) return;

        console.log("Plotting marker:", area.areaName, position);

        const marker = new window.google.maps.Marker({
          position,
          map,
        });

        marker.addListener("click", () => {
          infoWindow.setContent(
            `<div><strong>${area.areaName}</strong><br/>Pincode: ${area.pincode}</div>`
          );
          infoWindow.open(map, marker);
        });

        markersRef.current.push(marker);
        bounds.extend(position);
      });

      if (!bounds.isEmpty()) map.fitBounds(bounds);
    }
  }, [mapCenter, formik.values.areas]);

  const fetchPincodes = async (cityName) => {
    try {
      setMessage("Fetching pincodes, please wait...");
      await getPincodes({ cityName }).unwrap();
    } catch (error) {
      console.error("Error fetching pincodes:", error);
    }
  };

  const handleResult = (data) => {
    if (!data || !data.postOffices) return;

    const newAreas = data.postOffices
      .map((office) => {
        const lat = parseFloat(office.latitude ?? 0);
        const lng = parseFloat(office.longitude ?? 0);

        if (!lat || !lng) return null;

        return {
          areaName: office.Name,
          pincode: office.Pincode,
          coordinates: [lng, lat],
          latitude: office.latitude,
          longitude: office.longitude,
        };
      })
      .filter(Boolean);

    // Create the object to push to mainArray
    const mainObj = {
      pincode: data.pincode,
      areas: newAreas,
      totalAreas: newAreas.length,
    };

    // Merge without duplicates based on pincode
    const updatedMains = [
      ...formik.values.mainArray.filter(
        (existing) => existing.pincode !== mainObj.pincode
      ),
      mainObj,
    ];

    const updatedAreas = [
      ...formik.values.areas.filter(
        (a) =>
          !newAreas.some(
            (na) => na.pincode === a.pincode && na.areaName === a.areaName
          )
      ),
      ...newAreas,
    ];

    formik.setFieldValue("areas", updatedAreas);
    formik.setFieldValue("mainArray", updatedMains);
  };

  useEffect(() => {
    if (pincodeData?.data?.length > 0) {
      formik.setFieldValue("mainArray", pincodeData.data);
    }
  }, [pincodeData?.data]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {open && (
        <PincodeChecker
          open={open}
          onClose={() => setOpen(false)}
          onResult={handleResult}
          city={formik.values.cityName}
        />
      )}

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }} component="form" onSubmit={formik.handleSubmit}>
          <Typography variant="h5" gutterBottom>
            Add City
          </Typography>
          <TextField
            inputRef={cityInputRef}
            label="Select City"
            fullWidth
            name="cityName"
            value={formik.values.cityName}
            // InputProps={{ readOnly: true }} // ✅ IMPORTANT
            onChange={formik.handleChange}

            sx={{ mb: 2 }}
            error={Boolean(formik.errors.cityName)}
            helperText={formik.errors.cityName}
          />

          {/* Pincode selection */}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Available Pincodes:
            </Typography>
            <Button onClick={() => setOpen(true)}>+ Add Pincode</Button>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}
            >
              {pincodeData?.data?.length > 0 &&
                pincodeData.data.map((pinObj, i) => (
                  <FormControlLabel
                    key={i}
                    control={
                      <Checkbox
                        checked={formik.values.areas.some(
                          (a) => a.pincode === pinObj.pincode
                        )}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const newAreas = pinObj.areas.map((area) => ({
                              areaName: area.areaName,
                              pincode: pinObj.pincode,
                              coordinates: [
                                parseFloat(area.longitude),
                                parseFloat(area.latitude),
                              ],
                            }));

                            const newMainObj = {
                              pincode: pinObj.pincode,
                              areas: newAreas,
                              totalAreas: newAreas.length,
                            };

                            formik.setFieldValue("areas", [
                              ...formik.values.areas,
                              ...newAreas,
                            ]);

                            formik.setFieldValue("mainArray", [
                              ...formik.values.mainArray.filter(
                                (m) => m.pincode !== pinObj.pincode
                              ),
                              newMainObj,
                            ]);
                          } else {
                            // Remove from both areas and mainArray
                            formik.setFieldValue(
                              "areas",
                              formik.values.areas.filter(
                                (a) => a.pincode !== pinObj.pincode
                              )
                            );
                            formik.setFieldValue(
                              "mainArray",
                              formik.values.mainArray.filter(
                                (m) => m.pincode !== pinObj.pincode
                              )
                            );
                          }
                        }}
                      />
                    }
                    label={`${pinObj.pincode}`}
                  />
                ))}
            </Box>
          </Box>

          {/* Meta fields */}
          <TextField
            label="Meta Title"
            fullWidth
            name="metaTitle"
            value={formik.values.metaTitle}
            onChange={formik.handleChange}
            error={Boolean(formik.errors.metaTitle)}
            helperText={formik.errors.metaTitle}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Meta Description"
            fullWidth
            name="metaDescription"
            value={formik.values.metaDescription}
            onChange={formik.handleChange}
            error={Boolean(formik.errors.metaDescription)}
            helperText={formik.errors.metaDescription}
            sx={{ mb: 2 }}
          />

          {/* Keywords */}
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField
              label="Keyword"
              value={formik.values.keywordInput}
              onChange={(e) =>
                formik.setFieldValue("keywordInput", e.target.value)
              }
            />
            <Button
              onClick={() => {
                const input = (formik.values.keywordInput || "").trim();
                if (input) {
                  const updated = [...formik.values.keywords, input];
                  formik.setFieldValue("keywords", updated);
                  formik.setFieldValue("keywordInput", "");
                }
              }}
            >
              Add
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {formik.values?.keywords?.map((kw, i) => (
              <Chip key={i} label={kw} />
            ))}
          </Box>

          {formik.errors.keywords && (
            <Typography color="error" variant="body2">
              {formik.errors.keywords}
            </Typography>
          )}

          {/* Meta fields */}
          <TextField
            label="GST Number"
            fullWidth
            type="string"
            name="gst_number"
            value={formik.values.gst_number}
            onChange={formik.handleChange}
            error={Boolean(formik.errors.gst_number)}
            helperText={formik.errors.gst_number}
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
          />

          {/* <TextField
            label="Monthly subscription amount of beautician in INR"
            fullWidth
            type="number"
            name="subscription_amount"
            value={formik.values.subscription_amount}
            onChange={formik.handleChange}
            error={Boolean(formik.errors.subscription_amount)}
            helperText={formik.errors.subscription_amount}
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
          />

          <TextField
            label="Monthly booking count limit for beautician"
            fullWidth
            type="number"
            name="booking_limit"
            value={formik.values.booking_limit}
            onChange={formik.handleChange}
            error={Boolean(formik.errors.booking_limit)}
            helperText={formik.errors.booking_limit}
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
          />

          <TextField
            label="Top up period in months for beautician"
            fullWidth
            type="number"
            name="top_up_period"
            value={formik.values.top_up_period}
            onChange={formik.handleChange}
            error={Boolean(formik.errors.top_up_period)}
            helperText={formik.errors.top_up_period}
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
          /> */}

          <Button
            type="submit"
            variant="contained"
            color="success"
            fullWidth
            sx={{ mt: 4 }}
          >
            {"Save City with Areas"}
          </Button>
        </Paper>
      </Container>
    </DashboardLayout>
  );
}

export default AddLocation;
